import React from 'react';
import { Event } from '../../../../../model/event';
import { Card } from '../../../../../components/Card/Card';
import { t } from '../../../../../locales/translate';
import { Icon } from '../../../../../components/Icon/Icon';
import { SortableList } from '../../../../../components/SortableList/SortableList';
import { Allocation } from '../../../../../model/contingent';
import { sum, groupBy, orderBy } from 'lodash';
import { sortNumerically, getSequences } from '../../../../../util/util';
import { Link } from 'react-router-dom';
import { ordersPath, eventPrintTicketPath } from '../../../../../routes';
import { runEventAllocationsExport, runEventSeatStatusesExport } from '../../../../../model/exports';

interface Props {
  event: Event;
}

function OrderPerItemStatusLink({
  itemStatus,
  event,
  row,
}: {
  itemStatus: keyof Allocation;
  event: Event;
  row: Allocation;
}): JSX.Element {
  return (
    <Link
      to={ordersPath({
        productId: event.sku,
        venueSectionId: row.section.id === 'total' ? undefined : row.section.id,
        itemStatus,
      })}
    >
      {row[itemStatus]}
    </Link>
  );
}

function displayRows(seats: Array<{ row: string; name: string }>): React.ReactNode {
  const rows = groupBy(seats, 'row');

  return sortNumerically(Object.keys(rows)).map((row) => {
    return (
      <div key={row}>
        <strong>{row}: </strong>
        {getSequences(rows[row].map((e) => e.name)).join(', ')}
      </div>
    );
  });
}

export function AllocationList({ event }: Props): JSX.Element | null {
  const total: Allocation | undefined = event.allocations && {
    section: { id: 'total', name: 'Total' },
    available: sum(event.allocations.map((e) => e.available)),
    capacity: sum(event.allocations.map((e) => e.capacity)),
    systemSold: sum(event.allocations.map((e) => e.systemSold)),
    systemFree: sum(event.allocations.map((e) => e.systemFree)),
    processing: sum(event.allocations.map((e) => e.processing)),
    local: sum(event.allocations.map((e) => e.local)),
    cart: sum(event.allocations.map((e) => e.cart)),
    blocked: sum(event.allocations.map((e) => e.blocked)),
    reserved: sum(event.allocations.map((e) => e.reserved)),
    blockedSeats: [],
  };

  const actions = (
    <>
      <button className="button button--small" onClick={() => runEventAllocationsExport({ eventId: event.id })}>
        <Icon name="download--sm" />
        {t.model.allocation.csv}
      </button>
      <button className="button button--small" onClick={() => runEventSeatStatusesExport({ eventId: event.id })}>
        <Icon name="download--sm" />
        {t.model.venueLayout.seatsCsv}
      </button>
    </>
  );

  const lines = event.allocations && total ? [...orderBy(event.allocations, 'section.name'), total] : [];
  const allAllocationsPresent =
    event.allocations && event.venuePlan?.sections?.every((s) => event.allocations?.some((a) => a.section.id === s.id));

  return (
    <Card
      title={t.model.venueLayout.seatsAndBlocks}
      fullbleed
      actions={allAllocationsPresent ? actions : <Icon name="ticket" loading />}
    >
      {allAllocationsPresent && (
        <SortableList
          items={lines}
          rowClass={(row) => row.section.id === 'total' && 'sortable-list__footer'}
          columns={[
            { dataKey: 'section', label: t.model.allocation._name, cellRenderer: (row) => row.section.name },
            {
              dataKey: 'capacity',
              label: t.model.contingent.capacity,
            },
            {
              dataKey: 'available',
              label: t.model.status.available,
            },
            {
              dataKey: 'systemSold',
              label: (
                <div>
                  {t.model.status.systemSold}
                  <br />
                  {t.model.status.systemFree}
                </div>
              ),
              cellRenderer: (row) => (
                <div>
                  <OrderPerItemStatusLink itemStatus="systemSold" event={event} row={row} />
                  {row.processing > 0 && <span> + {row.processing}</span>}
                  <br />
                  <OrderPerItemStatusLink itemStatus="systemFree" event={event} row={row} />
                </div>
              ),
            },
            {
              dataKey: 'local',
              label: t.model.status.local,
              cellRenderer: (row) => (
                <div>
                  <Link to={eventPrintTicketPath(event)}>{row.local}</Link>
                </div>
              ),
            },
            {
              dataKey: 'reserved',
              label: t.model.status.reserved,
              cellRenderer: (row) => (
                <div>
                  <OrderPerItemStatusLink itemStatus="reserved" event={event} row={row} />
                </div>
              ),
            },
            {
              dataKey: 'cart',
              label: t.model.status.cart,
              cellRenderer: (row) => (
                <div>
                  <OrderPerItemStatusLink itemStatus="cart" event={event} row={row} />
                </div>
              ),
            },
            {
              dataKey: 'blocked',
              label: t.model.status.blocked,
              cellRenderer: (row) => <div>{row.blocked || 0}</div>,
            },
          ]}
          expandable={(row) => row.blockedSeats.length > 0}
          expandedContent={(row) => {
            if (row.blockedSeats) {
              return (
                <div>
                  {row.blockedSeats.map((blocking) => {
                    return (
                      <div key={blocking.blockReason}>
                        <div>
                          <strong>{blocking.blockReason}</strong>
                        </div>
                        {displayRows(blocking.seats)}
                      </div>
                    );
                  })}
                </div>
              );
            } else {
              return <div />;
            }
          }}
        />
      )}
    </Card>
  );
}
